/* eslint-disable no-template-curly-in-string */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import 'react-datetime/css/react-datetime.css';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';
import * as yup from 'yup';
import { setLocale } from 'yup'; // 参考：https://github.com/jquense/yup#error-message-customization
import LoginedLayout from './components/layouts/LoginedLayout';
import HomePage from './components/pages/home/HomePage';
import IllustIndexPage from './components/pages/illusts/index/IllustIndexPage';
import LoginTestPage from './components/pages/login-test/LoginTestPage';
import LogPage from './components/pages/logs/LogPage';
import ManualListPage from './components/pages/manuals/ManualListPage';
import MastersListPage from './components/pages/masters/MastersListPage';
import MasterFormPage from './components/pages/masters/edit/MasterFormPage';
import MasterSchedulePage from './components/pages/masters/schedule/MasterSchedulePage';
import NotFoundByLangPage from './components/pages/not-found-by-lang/NotFoundByLangPage';
import NotFoundPage from './components/pages/not-found/NotFoundPage';
import OrganizationListPage from './components/pages/organizations/OrganizationListPage';
import OrganizationTreePage from './components/pages/organizations/tree/OrganizationTreePage';
import PrivilegesEditPage from './components/pages/privileges/edit/PrivilegesEditPage';
import PrivilegesIndexPage from './components/pages/privileges/index/PrivilegesIndexPage';
import ProductionsAddPage from './components/pages/productions/add/ProductionsAddPage';
import BaseManualPage from './components/pages/productions/basemanual/BaseManualPage';
import ProductionsEditPage from './components/pages/productions/edit/ProductionsEditPage';
import ProductionsIndexPage from './components/pages/productions/index/ProductionsIndexPage';
import SectionPage from './components/pages/productions/section/SectionPage';
import PsDetailsPage from './components/pages/ps/details/PsDetailsPage';
import PsTemporaryNotesPage from './components/pages/ps/details/PsTemporaryNotesPage';
import PsListPage from './components/pages/ps/index/PsListPage';
import ScheduleListPage from './components/pages/schedulelist/ScheduleListPage';
import UserListPage from './components/pages/users/UserListPage';
import UserFormPage from './components/pages/users/edit/UserFormPage';
import UserNewFormPage from './components/pages/users/new/UserNewFormPage';
import i18n from './i18n'; // 参考：https://react.i18next.com/latest/using-with-hooks
import reportWebVitals from './reportWebVitals';
import './styles/fontawesome-5.15.4-web/css/all.css';
import './styles/fontawesome-6.2.0-web/css/all.css';
import './styles/style.css';

const queryClient = new QueryClient();

const getRoutesByLang = (lang: string): RouteObject => ({
  path: lang,
  children: [
    {
      path: 'login',
      element: <LoginTestPage />,
    },
    {
      path: '*',
      element: <LoginedLayout />,
      children: [
        { path: 'home', element: <HomePage /> },
        {
          path: 'productions',
          children: [
            { index: true, element: <ProductionsIndexPage /> },
            { path: 'add', element: <ProductionsAddPage /> },
            {
              path: 'edit/:productionId',
              element: <ProductionsEditPage />,
              children: [
                {
                  path: 'approval-application/add/:approvalApplicationTypeId',
                  element: <ProductionsEditPage />,
                },
                {
                  path: 'approval-application/edit/:approvalApplicationId',
                  element: <ProductionsEditPage />,
                },
                {
                  path: 'approval-application/view/:approvalApplicationId',
                  element: <ProductionsEditPage />,
                },
                {
                  path: 'confirmation-request/add/:confirmationRequestDivisionId',
                  element: <ProductionsEditPage />,
                },
                {
                  path: 'confirmation-request/edit/:confirmationRequestId',
                  element: <ProductionsEditPage />,
                },
                {
                  path: 'confirmation-request/view/:confirmationRequestId',
                  element: <ProductionsEditPage />,
                },
              ],
            },
            {
              path: 'base-manual/:productionId',
              element: <BaseManualPage />,
              children: [
                { path: 'flist', element: <BaseManualPage /> },
                { path: 'base', element: <BaseManualPage /> },
              ],
            },
            {
              path: 'sections/:productionId',
              element: <SectionPage />,
              children: [
                { path: 'ps', element: <SectionPage /> },
                { path: 'ori', element: <SectionPage /> },
                { path: 'psnote', element: <SectionPage /> },
              ],
            },
          ],
        },
        {
          path: 'schedulelist',
          element: <ScheduleListPage />,
        },
        {
          path: 'ps',
          children: [
            { index: true, element: <PsListPage /> },
            { path: 'view', element: <PsDetailsPage /> },
            { path: 'view/:psVersionId', element: <PsDetailsPage /> },
            { path: 'view/:psVersionId/psnote', element: <PsTemporaryNotesPage /> },
            { path: 'add/:psVersionId/:type', element: <PsDetailsPage /> },
            { path: 'edit/:psVersionId', element: <PsDetailsPage /> },
            { path: 'diff/:olderPsVersionId/:newerPsVersionId', element: <PsDetailsPage /> },
            { path: 'difftree/:olderPsVersionId/:newerPsVersionId', element: <PsDetailsPage /> },
          ],
        },
        {
          path: 'masterslist',
          children: [
            { index: true, element: <MastersListPage /> },
            { path: 'edit/:masterVersionId', element: <MasterFormPage /> },
            { path: 'schedule/:masterVersionId', element: <MasterSchedulePage /> },
          ],
        },
        {
          path: 'privileges',
          children: [
            { index: true, element: <PrivilegesIndexPage /> },
            { path: 'edit/:privilegeId', element: <PrivilegesEditPage /> },
          ],
        },
        {
          path: 'users',
          children: [
            { index: true, element: <UserListPage /> },
            {
              path: 'new',
              element: <UserNewFormPage />,
            },
            {
              path: 'edit/:userId',
              element: <UserFormPage />,
            },
          ],
        },
        {
          path: 'organizations',
          children: [
            { index: true, element: <OrganizationListPage /> },
            { path: 'tree', element: <OrganizationTreePage /> },
          ],
        },
        {
          path: 'illusts',
          children: [{ index: true, element: <IllustIndexPage /> }],
        },
        {
          path: 'manuals',
          children: [{ index: true, element: <ManualListPage /> }],
        },
        {
          path: 'logs',
          children: [{ index: true, element: <LogPage /> }],
        },
        { path: '*', element: <NotFoundByLangPage /> },
      ],
      loader: async () => {
        await i18n.changeLanguage(lang);
        setLocale({
          mixed: {
            required: i18n.t('この項目は必須です').toString(),
          },
          string: {
            max: i18n.t('${max}文字以下で入力してください。').toString(),
          },
        });
        // 参考：https://zenn.dev/longbridge/articles/f23ebecd5ad953
        yup.addMethod<yup.StringSchema>(yup.string, 'half', function validateHalf() {
          return this.matches(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/, { message: i18n.t('半角英数字記号で入力してください') });
        });
        yup.addMethod<yup.StringSchema>(yup.string, 'date', function valudateDateString() {
          return this.matches(/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, {
            message: i18n.t('yyyy/MM/dd 形式で入力してください'),
          });
        });
        return null;
      },
    },
  ],
});

const router = createBrowserRouter(
  [
    getRoutesByLang('ja'),
    getRoutesByLang('en'),
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ],
  { basename: process.env.REACT_APP_ROUTER_BASEPATH }
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <RouterProvider router={router} />
      </HelmetProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
